/**
 * Facebook Login for Business, the browser half.
 *
 * Meta hands the result back two ways at once: the login callback carries a
 * one-time code, and a postMessage carries the ids of the account the customer
 * picked. Both are needed, so we collect the message while the dialog is open and
 * pair it with the code when the dialog closes.
 */

const SDK_SCRIPT_ID = 'facebook-jssdk'
const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js'

// Naming the usual cause, because it is by far the most common one and the
// customer is the only one who can do anything about it. An ad blocker, a
// privacy extension or a network that filters trackers all stop this script,
// and without it there is no dialog to open.
const SDK_UNREACHABLE =
  'Could not load Facebook. An ad blocker or privacy extension is the usual cause: allow connect.facebook.net and try again.'

// The only origins Meta's signup dialog posts from.
const TRUSTED_ORIGINS = ['https://www.facebook.com', 'https://web.facebook.com']

// How long the login callback waits for the account message when it arrives
// first. Long enough for a message already on its way, short enough that a
// customer who really did close the dialog is not left watching a spinner.
const SESSION_INFO_GRACE_MS = 3000

export class SignupCancelled extends Error {
  constructor() {
    super('Signup was cancelled')
    this.name = 'SignupCancelled'
  }
}

/**
 * Load and initialise the Facebook SDK. Safe to call repeatedly: the script is
 * only ever added once, and later calls wait on the same load.
 */
export function loadFacebookSdk({ appId, graphVersion }) {
  if (window.FB) return Promise.resolve(window.FB)

  if (!loadFacebookSdk._pending) {
    loadFacebookSdk._pending = new Promise((resolve, reject) => {
      const script = document.createElement('script')

      // A script that has already failed never fires another event, so a retry
      // has to start from a new element or it waits on one that will never
      // answer. Turning a blocker off and trying again is exactly when this
      // happens.
      const fail = () => {
        script.remove()
        loadFacebookSdk._pending = null
        reject(new Error(SDK_UNREACHABLE))
      }

      script.onload = () => {
        // Some blockers answer with an empty script rather than an error, which
        // loads successfully and leaves nothing to call.
        if (!window.FB) return fail()

        window.FB.init({ appId, version: graphVersion, cookie: true, xfbml: false })
        resolve(window.FB)
      }
      script.onerror = fail

      document.getElementById(SDK_SCRIPT_ID)?.remove()
      script.id = SDK_SCRIPT_ID
      script.async = true
      script.src = SDK_SRC
      document.body.appendChild(script)
    })
  }

  return loadFacebookSdk._pending
}

// Standard onboarding for a number that will only ever be used through the API.
export const FEATURE_STANDARD = 'whatsapp_embedded_signup'
// Coexistence: the customer keeps using the WhatsApp Business App on the same
// number. Only offer it for a number that has been live on the app for a week.
export const FEATURE_COEXISTENCE = 'whatsapp_business_app_onboard'

/**
 * Which onboarding Meta should run for the choice the customer made.
 *
 * Named rather than written inline at the call site because inverting it is
 * silent: the dialog opens either way, and the customer only finds out that
 * WhatsApp has left their phone once it is gone.
 */
export function featureTypeFor(coexistence) {
  return coexistence ? FEATURE_COEXISTENCE : FEATURE_STANDARD
}

/**
 * Open Facebook Login for a Page, and resolve with the code alone.
 *
 * Simpler than the WhatsApp signup because there is nothing to pair the code
 * with. Meta's dialog carries its own page picker, and which pages were granted
 * is a question only a token can answer, so the server reads it from the grant
 * rather than the browser reporting it.
 *
 * @returns {Promise<{code: string}>}
 */
export async function launchPageSignup({ appId, configId, graphVersion }) {
  const FB = await loadFacebookSdk({ appId, graphVersion })

  return new Promise((resolve, reject) => {
    FB.login(
      (response) => {
        const code = response?.authResponse?.code

        if (!code) {
          reject(new SignupCancelled())
          return
        }

        resolve({ code })
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
      }
    )
  })
}

/**
 * Open Meta's signup dialog and resolve with everything the server needs.
 *
 * @returns {Promise<{code: string, wabaId: string, phoneNumberId: string}>}
 */
export async function launchEmbeddedSignup({
  appId,
  configId,
  solutionId,
  graphVersion,
  featureType = FEATURE_STANDARD,
}) {
  const FB = await loadFacebookSdk({ appId, graphVersion })

  return new Promise((resolve, reject) => {
    let sessionInfo = null
    // A signup that made an account but never added a number to it. Worth
    // telling apart, because the customer has to go back to Meta and add one.
    let accountWithoutNumber = false
    let onSessionInfo = null
    let graceTimer = null

    const onMessage = (event) => {
      if (!TRUSTED_ORIGINS.includes(event.origin)) return

      let payload
      try {
        payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
      } catch {
        return // not ours; the dialog posts other traffic too
      }

      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return

      if (payload.event === 'CANCEL') {
        finish(() => reject(new SignupCancelled()))
        return
      }

      if (payload.event === 'ERROR') {
        finish(() => reject(new Error(payload.data?.error_message || 'Meta reported an error')))
        return
      }

      // Read from whatever message carries both ids rather than from a list of
      // event names. Meta names the finishing event differently per onboarding
      // type, coexistence included, and adds names over time: what makes a
      // message the one we want is that it has what we need in it.
      if (payload.data?.waba_id && payload.data?.phone_number_id) {
        sessionInfo = {
          wabaId: payload.data.waba_id,
          phoneNumberId: payload.data.phone_number_id,
        }
        onSessionInfo?.()
      } else if (payload.data?.waba_id) {
        accountWithoutNumber = true
      }
    }

    let settled = false
    const finish = (settle) => {
      if (settled) return
      settled = true
      clearTimeout(graceTimer)
      window.removeEventListener('message', onMessage)
      settle()
    }

    const giveUp = () =>
      finish(() =>
        reject(
          new Error(
            accountWithoutNumber
              ? 'Meta connected the account but no phone number was added to it'
              : 'Meta did not report which account was connected'
          )
        )
      )

    window.addEventListener('message', onMessage)

    FB.login(
      (response) => {
        const code = response?.authResponse?.code

        if (!code) {
          finish(() => reject(new SignupCancelled()))
          return
        }

        if (sessionInfo) {
          finish(() => resolve({ code, ...sessionInfo }))
          return
        }

        // The code and the account travel on two channels that do not wait for
        // each other, so a callback that arrives first is not proof the message
        // is never coming. Give it a moment to land before giving up on a
        // signup the customer has already completed.
        graceTimer = setTimeout(giveUp, SESSION_INFO_GRACE_MS)
        onSessionInfo = () => finish(() => resolve({ code, ...sessionInfo }))
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: solutionId ? { solutionID: solutionId } : {},
          sessionInfoVersion: 3,
          featureType,
        },
      }
    )
  })
}
